/**
 * Página de Administración de Horas Sindicales y Asambleas
 * Permite configurar los parámetros por año:
 * - Horas por reunión sindical
 * - Máximo de horas por reuniones
 * - Horas por asamblea
 * - Máximo de horas por asambleas
 * - Horas por jornada (para consumo)
 */

import { useState, useEffect } from 'react';
import { useToast } from '../context/ToastContext';
import { horasSindicales as hsApi } from '../services/api';
import {
    Clock,
    Save,
    RefreshCw,
    Loader2,
    Info,
    Briefcase,
    Users,
    ChevronLeft,
    ChevronRight,
    Settings,
    CheckCircle
} from 'lucide-react';

const HorasSindicalesConfig = () => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [config, setConfig] = useState({
        horas_por_reunion: 2,
        max_horas_reunion: 40,
        horas_por_asamblea: 2,
        max_horas_asamblea: 16,
        horas_por_jornada: 8,
        arrastre: false,
    });
    const [originalConfig, setOriginalConfig] = useState(null);

    useEffect(() => {
        loadConfig();
    }, [selectedYear]);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const data = await hsApi.getConfig(selectedYear);
            const parsed = {
                horas_por_reunion: Number(data.horas_por_reunion) || 2,
                max_horas_reunion: Number(data.max_horas_reunion) || 40,
                horas_por_asamblea: Number(data.horas_por_asamblea) || 2,
                max_horas_asamblea: Number(data.max_horas_asamblea) || 16,
                horas_por_jornada: Number(data.horas_por_jornada) || 8,
                arrastre: Boolean(data.arrastre),
            };
            setConfig(parsed);
            setOriginalConfig(parsed);
        } catch (error) {
            console.error('Error loading config:', error);
            toast.error('Error al cargar configuración: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await hsApi.updateConfig(selectedYear, config);
            toast.success(`Configuración ${selectedYear} guardada correctamente`);
            setOriginalConfig({ ...config });
        } catch (error) {
            console.error('Error saving config:', error);
            toast.error('Error al guardar: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = originalConfig && JSON.stringify(config) !== JSON.stringify(originalConfig);

    const handleChange = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
                <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 lg:p-6" style={{ background: 'var(--bg-primary)' }}>
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                            ⏰ Horas Sindicales — Configuración
                        </h1>
                        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                            Configura los parámetros de horas por reuniones y asambleas
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={loadConfig}
                            className="p-2 rounded-lg transition-colors"
                            style={{ background: 'var(--bg-tertiary)' }}
                            title="Actualizar"
                        >
                            <RefreshCw className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                        </button>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setSelectedYear(y => y - 1)}
                                className="p-2 rounded-lg transition-colors hover:bg-white/10"
                                style={{ background: 'var(--bg-tertiary)' }}
                            >
                                <ChevronLeft className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                            </button>
                            <span
                                className="px-4 py-2 rounded-lg font-bold text-lg"
                                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                            >
                                {selectedYear}
                            </span>
                            <button
                                onClick={() => setSelectedYear(y => y + 1)}
                                className="p-2 rounded-lg transition-colors hover:bg-white/10"
                                style={{ background: 'var(--bg-tertiary)' }}
                            >
                                <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Reuniones Sindicales Section */}
                <div
                    className="rounded-2xl border overflow-hidden"
                    style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
                >
                    <div className="p-4 border-b flex items-center gap-3" style={{ borderColor: 'var(--border-color)' }}>
                        <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                                Reuniones Sindicales
                            </h2>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                Horas generadas por asistir a reuniones del sindicato
                            </p>
                        </div>
                    </div>
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                                Horas por cada reunión
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={config.horas_por_reunion}
                                onChange={(e) => handleChange('horas_por_reunion', parseFloat(e.target.value) || 0)}
                                className="w-full px-4 py-3 rounded-xl border text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                style={{
                                    background: 'var(--bg-secondary)',
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-primary)'
                                }}
                            />
                            <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
                                Horas de fiesta que se generan por asistir a una reunión
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                                Máximo de horas acumulables
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="1"
                                value={config.max_horas_reunion}
                                onChange={(e) => handleChange('max_horas_reunion', parseFloat(e.target.value) || 0)}
                                className="w-full px-4 py-3 rounded-xl border text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                style={{
                                    background: 'var(--bg-secondary)',
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-primary)'
                                }}
                            />
                            <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
                                Tope máximo de horas que un trabajador puede acumular por reuniones
                            </p>
                        </div>
                    </div>
                    <div className="px-6 pb-4">
                        <div
                            className="rounded-xl p-3 text-sm flex items-start gap-2"
                            style={{ background: 'rgba(59, 130, 246, 0.1)', color: 'rgb(96, 165, 250)' }}
                        >
                            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                                Ejemplo: con {config.horas_por_reunion}h/reunión y máx. {config.max_horas_reunion}h, 
                                un trabajador puede acumular hasta {Math.floor(config.max_horas_reunion / (config.horas_por_reunion || 1))} reuniones.
                            </span>
                        </div>
                    </div>
                </div>

                {/* Asambleas Section */}
                <div
                    className="rounded-2xl border overflow-hidden"
                    style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
                >
                    <div className="p-4 border-b flex items-center gap-3" style={{ borderColor: 'var(--border-color)' }}>
                        <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                            <Users className="w-5 h-5 text-violet-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                                Asambleas de Trabajadores
                            </h2>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                Horas generadas por asistir a asambleas convocadas por el comité
                            </p>
                        </div>
                    </div>
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                                Horas por cada asamblea
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={config.horas_por_asamblea}
                                onChange={(e) => handleChange('horas_por_asamblea', parseFloat(e.target.value) || 0)}
                                className="w-full px-4 py-3 rounded-xl border text-lg font-bold focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                                style={{
                                    background: 'var(--bg-secondary)',
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-primary)'
                                }}
                            />
                            <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
                                Horas de fiesta que se generan por asistir a una asamblea
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                                Máximo de horas acumulables
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="1"
                                value={config.max_horas_asamblea}
                                onChange={(e) => handleChange('max_horas_asamblea', parseFloat(e.target.value) || 0)}
                                className="w-full px-4 py-3 rounded-xl border text-lg font-bold focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                                style={{
                                    background: 'var(--bg-secondary)',
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-primary)'
                                }}
                            />
                            <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
                                Tope máximo de horas que un trabajador puede acumular por asambleas
                            </p>
                        </div>
                    </div>
                    <div className="px-6 pb-4">
                        <div
                            className="rounded-xl p-3 text-sm flex items-start gap-2"
                            style={{ background: 'rgba(139, 92, 246, 0.1)', color: 'rgb(167, 139, 250)' }}
                        >
                            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                                Ejemplo: con {config.horas_por_asamblea}h/asamblea y máx. {config.max_horas_asamblea}h, 
                                un trabajador puede acumular hasta {Math.floor(config.max_horas_asamblea / (config.horas_por_asamblea || 1))} asambleas.
                            </span>
                        </div>
                    </div>
                </div>

                {/* General Config Section */}
                <div
                    className="rounded-2xl border overflow-hidden"
                    style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
                >
                    <div className="p-4 border-b flex items-center gap-3" style={{ borderColor: 'var(--border-color)' }}>
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                            <Settings className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                                Configuración General
                            </h2>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                Parámetros generales del sistema de horas sindicales
                            </p>
                        </div>
                    </div>
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                                Horas por jornada completa
                            </label>
                            <input
                                type="number"
                                min="1"
                                step="0.5"
                                value={config.horas_por_jornada}
                                onChange={(e) => handleChange('horas_por_jornada', parseFloat(e.target.value) || 8)}
                                className="w-full px-4 py-3 rounded-xl border text-lg font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                                style={{
                                    background: 'var(--bg-secondary)',
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-primary)'
                                }}
                            />
                            <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
                                Horas que se consumen al solicitar un día libre por horas sindicales
                            </p>
                        </div>
                        <div className="flex items-center">
                            <div
                                className="rounded-xl p-4 w-full"
                                style={{ background: 'var(--bg-secondary)' }}
                            >
                                <p className="text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
                                    Resumen {selectedYear}
                                </p>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span style={{ color: 'var(--text-muted)' }}>Máx. total acumulable:</span>
                                        <span className="font-bold text-cyan-400">
                                            {config.max_horas_reunion + config.max_horas_asamblea}h
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span style={{ color: 'var(--text-muted)' }}>Días libres posibles:</span>
                                        <span className="font-bold text-emerald-400">
                                            {Math.floor((config.max_horas_reunion + config.max_horas_asamblea) / (config.horas_por_jornada || 1))} días
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end gap-3">
                    {hasChanges && (
                        <button
                            onClick={loadConfig}
                            className="px-6 py-3 rounded-xl font-medium transition-all border"
                            style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                        >
                            Descartar cambios
                        </button>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        className={`px-8 py-3 rounded-xl font-bold text-white flex items-center gap-2 transition-all ${
                            hasChanges
                                ? 'bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 shadow-lg shadow-blue-500/20'
                                : 'bg-gray-600 cursor-not-allowed opacity-50'
                        }`}
                    >
                        {saving ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : hasChanges ? (
                            <Save className="w-5 h-5" />
                        ) : (
                            <CheckCircle className="w-5 h-5" />
                        )}
                        {saving ? 'Guardando...' : hasChanges ? 'Guardar configuración' : 'Sin cambios'}
                    </button>
                </div>

                {/* Info Panel */}
                <div
                    className="rounded-2xl border p-5"
                    style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
                >
                    <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                        <Info className="w-5 h-5 text-blue-400" />
                        ¿Cómo funciona el sistema?
                    </h3>
                    <div className="space-y-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                        <p>
                            <strong className="text-blue-400">1.</strong> Cuando un conductor marca un día con motivo <strong style={{ color: 'var(--text-primary)' }}>"Reunión Sindical"</strong> o <strong style={{ color: 'var(--text-primary)' }}>"Asamblea"</strong>, 
                            el sistema suma automáticamente las horas configuradas a su bolsa.
                        </p>
                        <p>
                            <strong className="text-blue-400">2.</strong> Las horas de reuniones y asambleas se acumulan en una <strong style={{ color: 'var(--text-primary)' }}>bolsa unificada</strong>, 
                            pero cada concepto tiene su propio tope máximo.
                        </p>
                        <p>
                            <strong className="text-blue-400">3.</strong> Cuando el conductor acumule al menos <strong style={{ color: 'var(--text-primary)' }}>{config.horas_por_jornada}h</strong>, 
                            podrá marcar un día con motivo <strong style={{ color: 'var(--text-primary)' }}>"Día por Horas Sindicales"</strong> para consumir esas horas y obtener un día libre.
                        </p>
                        <p>
                            <strong className="text-blue-400">4.</strong> La validación se realiza automáticamente: si no tiene suficientes horas, 
                            el sistema <strong style={{ color: 'var(--text-primary)' }}>no le permitirá</strong> marcar el día de consumo.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HorasSindicalesConfig;
